import React, { useEffect, useMemo } from 'react';
import clsx from 'clsx';
import { QRCodeSVG } from 'qrcode.react';
import { PhonicsLesson } from '@/api/types';
import { useLiveLesson } from '@/context/LiveLesson';
import { useLessonPlayer } from '@/context/LessonPlayer';
import { CarouselSlide } from '../../Carousel';
import { Spinner } from '../../Spinner';

interface LearningLauncherProps {
  lesson: PhonicsLesson;
}

export const LearningLauncher = ({}: LearningLauncherProps) => {
  const { registerSlide } = useLessonPlayer();

  const {
    liveLessonStatus,
    beginLiveLesson,
    beginLiveLessonLoading,
    beginLiveLessonError,
  } = useLiveLesson();

  const qrCodeUrl = useMemo(() => {
    if (!window) {
      return;
    }

    return `${window.location.origin}/phonics/join?accessCode=${liveLessonStatus?.accessCode}`;
  }, [liveLessonStatus]);

  useEffect(() => {
    registerSlide('learning-launcher');

    beginLiveLesson();
  }, [registerSlide]);

  return (
    <CarouselSlide
      identifier="learning-launcher"
      className="relative flex flex-col justify-center items-center px-12 bg-[var(--c-action)]"
      classNameInner="flex-grow w-[760px] max-h-[500px] xl:w-[960px] xl:max-h-[800px] 2xl:w-[1280px]"
    >
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage:
            'radial-gradient(circle at 12% 18%, rgba(255,255,255,0.07) 0, transparent 200px),' +
            'radial-gradient(circle at 88% 78%, rgba(255,255,255,0.07) 0, transparent 200px)',
        }}
      />

      {beginLiveLessonError ? (
        <p className="text-white text-2xl">
          Unable to create live lesson. Please try again.
        </p>
      ) : beginLiveLessonLoading || !liveLessonStatus ? (
        <Spinner size="3xl" />
      ) : liveLessonStatus ? (
        <div className="relative flex flex-col items-center">
          <div className="flex items-center gap-8">
            <div className="flex items-center gap-4">
              <span
                className="text-3xl uppercase tracking-[3px] text-white/90"
                style={{ fontFamily: 'var(--lp-display), Impact, sans-serif' }}
              >
                Access code
              </span>

              <div
                className="rounded-2xl bg-white px-8 py-3 text-6xl leading-none text-pt-orange-dark shadow-[0_12px_32px_rgba(0,0,0,0.18)]"
                style={{
                  fontFamily: 'var(--lp-display), Impact, sans-serif',
                  letterSpacing: '4px',
                }}
              >
                {liveLessonStatus.accessCode}
              </div>
            </div>

            {qrCodeUrl && (
              <div className="rounded-2xl bg-white p-4 shadow-[0_12px_32px_rgba(0,0,0,0.18)]">
                <QRCodeSVG
                  value={qrCodeUrl}
                  size={220}
                  bgColor="#ffffff"
                  fgColor="#1A1A1A"
                />
              </div>
            )}
          </div>

          <h1
            className="my-10 text-center text-7xl uppercase tracking-wide text-white"
            style={{ fontFamily: 'var(--lp-display), Impact, sans-serif' }}
          >
            Who&apos;s ready?
          </h1>

          <div className="grid grid-cols-4 gap-6">
            {liveLessonStatus?.studentLiveStatuses.map(
              (studentStatus, index) => (
                <div
                  key={index}
                  className={clsx(
                    'flex items-center justify-center gap-2 rounded-full px-5 py-3 text-center text-2xl font-inter font-semibold',
                    studentStatus.isOnline
                      ? 'bg-white text-pt-ds-ink'
                      : 'bg-white/20 text-white/80',
                  )}
                >
                  <span
                    className={clsx(
                      'h-3 w-3 rounded-full',
                      studentStatus.isOnline
                        ? 'bg-[var(--c-success)]'
                        : 'bg-white/50',
                    )}
                  />
                  {studentStatus.name}
                </div>
              ),
            )}
          </div>
        </div>
      ) : null}
    </CarouselSlide>
  );
};
